import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Info, Scooter } from 'lucide-react';
import { useForm, FormProvider, type Resolver } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';

import Files from '@/components/forms/files';
import Select from '@/components/forms/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { usePostGarantieMobilite } from '@/hooks/api/use-contract-actions';
import { isActionAvailable } from '@/lib/contracts';
import { queryKeys } from '@/lib/query-keys';
import { garantieMobiliteSchema, type GarantieMobiliteValues } from '@/schemas/garantie-mobilite';
import type { Contract } from '@/types/api/contract';

const MOTIFS = [
    'J\'ai eu un problème de batterie',
    'J\'ai eu un problème de freins sur ma trottinette',
    'Ma trottinette n\'était pas disponible',
    'Autres motifs',
];

export interface GarantieMobiliteProps {
    contract: Contract;
}

const GarantieMobilite = ({ contract }: GarantieMobiliteProps) => {
    const id_contrat = contract.id;
    const eligible = isActionAvailable(contract, 'garantie_mobilite');
    const queryClient = useQueryClient();
    const { mutate: postGarantieMobiliteMutation, isPending: loading } = usePostGarantieMobilite();
    /* Le contrat reste monté en sortant de la sous-route : sans relecture,
       l'action resterait disponible et le parcours réaccessible
       alors que la garantie vient d'être consommée. */
    const postGarantieMobilite = ({ form, successFunction }: { form: GarantieMobiliteValues; successFunction?: () => void }) =>
        postGarantieMobiliteMutation({
            contractId: id_contrat,
            form,
            successFunction: () => {
                void queryClient.invalidateQueries({ queryKey: queryKeys.contract(id_contrat) })
                if (successFunction) successFunction()
            }
        })
    const navigate = useNavigate();

    const methods = useForm<GarantieMobiliteValues>({
        defaultValues: { circonstances: '', files: {} },
        resolver: zodResolver(garantieMobiliteSchema) as unknown as Resolver<GarantieMobiliteValues>,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const { handleSubmit, formState: { errors }, watch, setValue } = methods;
    const values = watch();

    /* Garantie non disponible selon le Pass : on ne laisse pas
       l'accès direct à l'URL contourner le contrôle (le motif est affiché sur le détail contrat). */
    if (!eligible) {
        return <Navigate to={`/mes-contrats/mon-contrat/${id_contrat}`} replace />
    }

    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-white mt-12">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-sm opacity-90 mb-1">Mon contrat</p>
                    <h1 className="text-3xl font-semibold flex items-center gap-3">
                        <Scooter className="w-8 h-8" />
                        Ma garantie mobilité
                    </h1>
                </div>
            </div>

            <Alert variant="warning">
                <Info className="w-4 h-4" />
                <AlertDescription>
                    Votre garantie mobilité vous permet d'être remboursé de vos frais de transport alternatif
                    lorsque vous n'avez pas pu utiliser votre trottinette. Elle ne peut être utilisée
                    qu'une seule fois sur la durée de votre contrat.
                </AlertDescription>
            </Alert>

            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(values => postGarantieMobilite({
                        form: values,
                        successFunction: () => navigate(`/mes-contrats/mon-contrat/${id_contrat}`)
                    }))}
                    className="space-y-6"
                >
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg">Pourquoi n'avez-vous pas pu utiliser votre trottinette ?</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-6">
                            <Select
                                name={'circonstances'}
                                label={'Motif'}
                                required
                                value={values.circonstances}
                                error={errors.circonstances}
                            >
                                <option value="">Choisissez un motif</option>
                                {MOTIFS.map(motif => (
                                    <option key={motif} value={motif}>{motif}</option>
                                ))}
                            </Select>

                            <div>
                                <p className="text-sm font-medium mb-2">
                                    Votre justificatif<sup className="text-destructive ml-0.5" aria-label="obligatoire">*</sup>
                                </p>
                                {/* Files mute l'objet reçu : on le clone pour que react-hook-form
                                    voie bien une nouvelle référence et repropage la valeur. */}
                                <Files
                                    label={'Choisissez votre justificatif'}
                                    name={'files[X]'}
                                    arrayName={'files'}
                                    code={'X'}
                                    value={values.files}
                                    setValue={(name, files) => setValue(name, { ...files })}
                                />
                                {errors.files && (
                                    <p role="alert" aria-live="assertive" className="text-xs text-destructive italic mt-1">
                                        {(errors.files as { message?: string }).message}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Button
                        type="submit"
                        className="w-full cursor-pointer"
                        loading={loading}
                        loadingText="Envoi en cours..."
                    >
                        <Scooter className="w-4 h-4" />
                        Je souhaite bénéficier de ma garantie mobilité
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
};

export default GarantieMobilite;
